import { Reveal, Eyebrow } from "@/components/Reveal";
import { DonateButton } from "@/components/DonateButton";
import { TIMELINE } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden bg-ember-subtle grain py-16">
        <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-teal-subtle blur-[90px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <Eyebrow>Our mission</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl lg:text-6xl">
              Education is not a privilege. <span className="text-teal">It is a right.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-7 max-w-3xl text-base leading-relaxed text-coal-muted md:text-lg">
              The EduPower Foundation was established on a simple but urgent conviction: that access
              to quality education is not a privilege, it is a right. In South Africa, where
              systemic inequality continues to shape what is possible for millions of young people,
              we act as a bridge between potential and opportunity.
            </p>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-coal-muted">
              Our vision is a just and equitable society where every learner, regardless of
              postcode or background, has access to the cultural capital and academic foundation
              they deserve. Everything we do is in service of that goal.
            </p>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-coal-muted">
              Our work extends beyond the learner. We invest in the educators who show up every day
              in under-resourced classrooms, providing professional development workshops in
              assessment design, pedagogy, AI integration and teacher wellbeing. When teachers
              thrive, learners thrive.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16" data-testid="timeline-section">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <Eyebrow>2022 to 2026</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
              The story so far
            </h2>
          </Reveal>
          <div className="relative mt-14 space-y-14 before:absolute before:left-[19px] before:top-2 before:h-[calc(100%-16px)] before:w-px before:bg-coal/10 sm:before:left-1/2">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={0.05}>
                <div
                  className={`relative flex flex-col gap-4 pl-14 sm:w-1/2 sm:pl-0 ${
                    i % 2 === 0 ? "sm:pr-14 sm:text-right" : "sm:ml-auto sm:pl-14"
                  }`}
                  data-testid={`timeline-item-${t.year}`}
                >
                  <span
                    className={`absolute top-1 flex h-10 w-10 items-center justify-center rounded-full font-display text-xs font-extrabold text-sand left-0 ${
                      i % 2 === 0
                        ? "bg-teal sm:left-auto sm:-right-5"
                        : "bg-ember sm:-left-5"
                    }`}
                  >
                    {t.year.slice(2)}
                  </span>
                  <div
                    className={`rounded-3xl p-7 shadow-[0_14px_44px_-24px_rgba(26,29,36,0.35)] ${
                      i % 2 === 0 ? "bg-white" : "bg-coal text-sand"
                    }`}
                  >
                    <span className={`font-display text-4xl font-extrabold ${i % 2 === 0 ? "text-teal" : "text-ember-light"}`}>
                      {t.year}
                    </span>
                    <h3 className={`mt-2 font-heading text-lg font-bold ${i % 2 === 0 ? "text-coal" : "text-sand"}`}>
                      {t.title}
                    </h3>
                    <p className={`mt-2 text-sm leading-relaxed ${i % 2 === 0 ? "text-coal-muted" : "text-sand/65"}`}>
                      {t.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-teal py-14" data-testid="about-cta">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-sand sm:text-4xl">
              Help us write the next chapter
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-sand/80 sm:text-base">
              From the Labia Theatre in 2022 to Robben Island in 2025, every milestone was powered
              by people who believed. Join them.
            </p>
          </Reveal>
          <DonateButton testId="about-donate-button">Donate now</DonateButton>
        </div>
      </section>
    </div>
  );
}
